/**
 * Orçamento aberto e editado, sobre o PostgREST — trilha T5a.
 *
 * Editar passa pela `editar_orcamento` da `0084`: itens, responsável, validade
 * e total mudam numa transação só, e o número (`0060`) não muda.
 */

import { supabase } from '@/supabase';
import { atual } from '@/session/sessao';
import type { PortaDeDados } from './porta';
import type { NovoOrcamento, Orcamento, StatusDoOrcamento } from './tipos';

type MetodosDeVendas = Pick<PortaDeDados, 'orcamento' | 'editarOrcamento'>;

const CAMPOS_DO_ORCAMENTO = 'id, numero, contato_id, status, total_centavos, validade, criado_em, contatos(nome), membros!responsavel_id(nome), '
    + 'orcamento_itens(id, procedimento_id, descricao, quantidade, valor_unitario_centavos, desconto_centavos, ordem)';

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

function clinicaId(): string {
    const id = atual()?.clinicaId;
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

function erro(mensagem: string, e: { message: string; code?: string } | null): void {
    if (!e) return;
    if (e.code === '42501') throw new Error('Editar orçamento exige a permissão de vendas.');
    if (e.code === 'P0001' || e.code === 'P0002' || e.code === '22023') throw new Error(e.message);
    throw new Error(`${mensagem}: ${e.message}`);
}

function nomeEmbutido(v: unknown): string | undefined {
    const x = Array.isArray(v) ? v[0] : v;
    return x && typeof x === 'object' ? ((x as { nome?: string }).nome ?? undefined) : undefined;
}

interface LinhaDoOrcamento {
    id: string;
    numero: string;
    contato_id: string | null;
    status: StatusDoOrcamento;
    total_centavos: number;
    validade: string | null;
    criado_em: string;
    contatos: unknown;
    membros: unknown;
    orcamento_itens: {
        id: string; procedimento_id: string | null; descricao: string; quantidade: number;
        valor_unitario_centavos: number; desconto_centavos: number; ordem: number;
    }[] | null;
}

function paraOrcamento(o: LinhaDoOrcamento): Orcamento {
    const itens = [...(o.orcamento_itens ?? [])].sort((a, b) => a.ordem - b.ordem);
    return {
        id: o.id,
        numero: o.numero,
        contatoId: o.contato_id ?? '',
        paciente: nomeEmbutido(o.contatos) ?? '—',
        responsavel: nomeEmbutido(o.membros) ?? '—',
        status: o.status,
        totalCentavos: o.total_centavos,
        ...(o.validade ? { validade: o.validade } : {}),
        criadoEm: o.criado_em.slice(0, 10),
        itens: itens.map((i) => ({
            id: i.id, descricao: i.descricao, quantidade: i.quantidade,
            ...(i.procedimento_id ? { procedimentoId: i.procedimento_id } : {}),
            valorUnitarioCentavos: i.valor_unitario_centavos, descontoCentavos: i.desconto_centavos,
        })),
        resumo: itens.map((i) => i.descricao).join(' · '),
    };
}

export const vendasNoSupabase: MetodosDeVendas = {
    async orcamento(id: string): Promise<Orcamento> {
        const { data, error } = await cliente().from('orcamentos').select(CAMPOS_DO_ORCAMENTO).eq('id', id).maybeSingle();
        erro('Falha ao ler o orçamento', error);
        if (!data) throw new Error('Orçamento não encontrado.');
        return paraOrcamento(data as unknown as LinhaDoOrcamento);
    },

    async editarOrcamento(id: string, dados: NovoOrcamento): Promise<Orcamento> {
        /* Por id quando a tela o tem (a equipe cadastrada); pelo nome só no que
           ainda chega sem ele. */
        let responsavelId: string | null = dados.responsavelId ?? null;
        if (!responsavelId && dados.responsavel && dados.responsavel !== '—') {
            const { data: membro, error: erroDoMembro } = await cliente()
                .from('membros').select('id').eq('clinica_id', clinicaId()).eq('nome', dados.responsavel).maybeSingle();
            erro('Falha ao ler o responsável', erroDoMembro);
            responsavelId = (membro as unknown as { id: string } | null)?.id ?? null;
            if (!responsavelId) throw new Error(`${dados.responsavel} não está na equipe desta clínica.`);
        }

        const { error } = await cliente().rpc('editar_orcamento', {
            p_orcamento: id,
            p_responsavel: responsavelId,
            p_validade: dados.validade ?? null,
            p_itens: dados.itens.map((i) => ({
                ...(i.procedimentoId ? { procedimentoId: i.procedimentoId } : {}),
                descricao: i.descricao,
                quantidade: i.quantidade,
                valorUnitarioCentavos: i.valorUnitarioCentavos,
                descontoCentavos: i.descontoCentavos,
            })),
        });
        erro('Falha ao salvar a proposta', error);
        return this.orcamento(id);
    },
};
